import {
  ContentBlock,
  EditorState,
  SelectionState,
  Modifier,
  CharacterMetadata,
  ContentState,
} from 'draft-js'

import { PluginFunctions } from '~/types'
import ConverterContext from '~/reactapp/story/util/ConverterContext'
import blockKeysFromSelection from '~/reactapp/DraftEditor/plugins/pluginCommon/blockKeysFromSelection'

const DROP_CAP = 'DROP_CAP'
const ansAllowedDropCapValue = 'letter'

function applyDropCap(block: ContentBlock) {
  return new ContentBlock(
    block
      .set(
        'characterList',
        block
          .getCharacterList()
          .set(
            0,
            CharacterMetadata.applyStyle(
              block.getCharacterList().first(),
              DROP_CAP
            )
          )
      )
      .merge({
        data: block
          .getData()
          .setIn(['blockProperties', 'dropcap'], ansAllowedDropCapValue),
      })
  )
}

function removeDropCap(block: ContentBlock) {
  let updatedBlockData
  if (block.getType() === 'paragraph' || block.getType() === 'unstyled') {
    updatedBlockData = block.getData().deleteIn(['blockProperties', 'dropcap'])
  } else {
    updatedBlockData = block.getData().delete('blockProperties')
  }

  let updatedBlock = new ContentBlock(
    block.merge({
      data: updatedBlockData,
    })
  )

  if (block.getLength()) {
    // Only remove character style if block has text
    // to prevent an error getting the characterList
    updatedBlock = new ContentBlock(
      updatedBlock.set(
        'characterList',
        block
          .getCharacterList()
          .set(
            0,
            CharacterMetadata.removeStyle(
              block.getCharacterList().first(),
              DROP_CAP
            )
          )
      )
    )
  }

  return updatedBlock
}

function resetDropCap(block: ContentBlock, editorState: EditorState) {
  const selection = editorState.getSelection()
  const contentState = editorState.getCurrentContent()

  const contentStateWithFirstCharStyled = Modifier.applyInlineStyle(
    contentState,
    new SelectionState(
      selection.merge({
        anchorOffset: 0,
        focusOffset: 1,
      })
    ),
    DROP_CAP
  )
  const contentStateWithFirstCharStyledAndRestUnstyled = Modifier.removeInlineStyle(
    contentStateWithFirstCharStyled,
    new SelectionState(
      selection.merge({
        anchorOffset: 1,
        focusOffset: block.getLength(),
      })
    ),
    DROP_CAP
  )

  // Ensure original selection is restored
  return EditorState.acceptSelection(
    EditorState.push(
      editorState,
      contentStateWithFirstCharStyledAndRestUnstyled,
      'change-inline-style'
    ),
    editorState.getSelection()
  )
}

export function blockHasAnsDropCap(block: ContentBlock) {
  if (block) {
    return (
      block.getData().getIn(['blockProperties', 'dropcap']) ===
      ansAllowedDropCapValue
    )
  }

  return false
}

export function afterAppendBlocksMiddleware(
  _element: unknown,
  insertedBlocks: ContentBlock[],
  context: ConverterContext
) {
  const insertedBlocksWithDropCapsApplied = insertedBlocks.map((block) => {
    return blockHasAnsDropCap(block) ? applyDropCap(block) : block
  })

  context.replaceBlocks(insertedBlocksWithDropCapsApplied)

  return insertedBlocksWithDropCapsApplied
}

export function setDropCapsForBlocks(
  blockKeys: string[],
  editorState: EditorState,
  dropCapResult: 'on' | 'off'
) {
  const contentStateWithDropCapsRemoved = blockKeys.reduce(
    (contentState, blockKey) => {
      const block = contentState.getBlockForKey(blockKey)
      const updatedContentBlock =
        dropCapResult === 'on' ? applyDropCap(block) : removeDropCap(block)

      return new ContentState(
        contentState.set(
          'blockMap',
          contentState.getBlockMap().set(blockKey, updatedContentBlock)
        )
      )
    },
    editorState.getCurrentContent()
  )

  // Ensure original selection is restored
  return EditorState.acceptSelection(
    EditorState.push(
      editorState,
      contentStateWithDropCapsRemoved,
      'change-inline-style'
    ),
    editorState.getSelection()
  )
}

export function createDropCapPlugin() {
  return {
    customStyleMap: {
      [DROP_CAP]: {
        float: 'left',
        fontSize: 'calc(var(--editor-font-size) * 5)',
        lineHeight: 'calc(var(--editor-font-size) * 4)',
      },
    },
    // This handler acts on text changes to a drop cap block (typing, cut/paste, text rearrange)
    // and applies or removes the inline style to the proper characters.
    onChange: (
      editorState: EditorState,
      pluginFunctions: PluginFunctions
    ): EditorState => {
      const currentContentState = editorState.getCurrentContent()
      const selection = editorState.getSelection()
      const selectedBlockKeys: string[] = blockKeysFromSelection(
        currentContentState,
        selection
      )
      const selectionIncludesDropCap = selectedBlockKeys.some((blockKey) => {
        const block = currentContentState.getBlockForKey(blockKey)
        return blockHasAnsDropCap(block)
      })

      if (!selectionIncludesDropCap) {
        return editorState
      }

      let nextEditorState = editorState
      if (editorState.getLastChangeType() === 'change-block-type') {
        // Remove all drop caps from selection
        nextEditorState = setDropCapsForBlocks(
          selectedBlockKeys,
          editorState,
          'off'
        )
      } else if (selectedBlockKeys.length === 1) {
        const currentBlockKey = selectedBlockKeys[0]
        const originalContentBlock = pluginFunctions
          .getEditorState()
          .getCurrentContent()
          .getBlockForKey(currentBlockKey)
        const updatedContentBlock = currentContentState.getBlockForKey(
          currentBlockKey
        )

        // First character has changed: dog -> log
        // OR same first character added at start of block: dog -> ddog
        const hasNewFirstChar =
          originalContentBlock.getText()[0] !==
            updatedContentBlock.getText()[0] ||
          (originalContentBlock.getText()[0] ===
            updatedContentBlock.getText()[0] &&
            originalContentBlock.getLength() !==
              updatedContentBlock.getLength())

        if (!updatedContentBlock.getLength()) {
          // Remove ANS property from empty block
          nextEditorState = setDropCapsForBlocks(
            [updatedContentBlock.getKey()],
            editorState,
            'off'
          )
        } else if (hasNewFirstChar) {
          nextEditorState = resetDropCap(updatedContentBlock, editorState)
        }
      }

      return nextEditorState
    },
  }
}
